/** @jsx jsx */
import React, { memo, useMemo } from 'react';
import { jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Spinner from '@atlaskit/spinner';
import { Pressable, xcss } from '@atlaskit/primitives';
import type { richText_nativeIssueTable_RichTextCell_fieldRef$key } from '@atlassian/jira-relay/src/__generated__/richText_nativeIssueTable_RichTextCell_fieldRef.graphql';
import type { richText_nativeIssueTable_RichTextCell_issueRef$key } from '@atlassian/jira-relay/src/__generated__/richText_nativeIssueTable_RichTextCell_issueRef.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { CellHoverRenderer, HoverPopover } from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { CellHoverProvider } from '@atlassian/jira-hover-popover/src/controllers/index.tsx';
import {
	useIsDensityFull,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { PACKAGE_NAME } from '../../../constants.tsx';
import { RichTextCellCompact } from './rich-text-compact/index.tsx';
import { RichTextCellFull } from './rich-text-full/index.tsx';
import { richTextPopUpContentEntryPoint } from './rich-text-popup-content/entrypoint.tsx';
import { ErrorState } from './error-state/index.tsx';

export type Props = {
	issueKey: string;
	fieldRef: richText_nativeIssueTable_RichTextCell_fieldRef$key;
	/**
	 * Should only be needed For VR testing, to render a storybook example with
	 * the popup content immediately visible
	 */
	shouldOpenOnFirstRender?: boolean;
	issueRef?: richText_nativeIssueTable_RichTextCell_issueRef$key | null;
	isReadOnlyWithoutHoverPopover?: boolean;
};

export const RichTextCell = memo<Props>(
	({
		issueKey,
		fieldRef,
		issueRef,
		shouldOpenOnFirstRender = false,
		isReadOnlyWithoutHoverPopover,
	}: Props) => {
		const { richText, fieldId } = useFragment<richText_nativeIssueTable_RichTextCell_fieldRef$key>(
			graphql`
				fragment richText_nativeIssueTable_RichTextCell_fieldRef on JiraRichTextField
				@argumentDefinitions(isDensityFull: { type: "Boolean!" }) {
					fieldId
					richText {
						adfValue {
							convertedPlainText @skip(if: $isDensityFull) {
								plainText
							}
							json @include(if: $isDensityFull)
						}
					}
				}
			`,
			fieldRef,
		);

		const issueData = useFragment<richText_nativeIssueTable_RichTextCell_issueRef$key>(
			graphql`
				fragment richText_nativeIssueTable_RichTextCell_issueRef on JiraIssue
				@argumentDefinitions(isDensityFull: { type: "Boolean!" }) {
					canBeExported @include(if: $isDensityFull)
				}
			`,
			issueRef || null,
		);

		const isDensityFull = useIsDensityFull();

		const isSingleLineRowHeightEnabled =
			useIsSingleLineRowHeightEnabled() && !isReadOnlyWithoutHoverPopover;
		const cloudId = useCloudId();
		const entryPointParams = useMemo(
			() => ({ fieldId, issueKey, cloudId }),
			[fieldId, cloudId, issueKey],
		);
		const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
			richTextPopUpContentEntryPoint,
			entryPointParams,
		);

		if (isSingleLineRowHeightEnabled && !isDensityFull) {
			const previewText = richText?.adfValue?.convertedPlainText?.plainText;

			return (
				<CellHoverProvider>
					<CellHoverRenderer>
						{(props) => (
							<HoverPopover<HTMLButtonElement>
								content={
									previewText && (
										<JiraEntryPointContainer
											entryPointReferenceSubject={entryPointReferenceSubject}
											id="rich-text-popup-content"
											packageName={PACKAGE_NAME}
											errorFallback={() => <ErrorState />}
											fallback={<Spinner size="medium" />}
											runtimeProps={{}}
										/>
									)
								}
								entryPointActions={entryPointActions}
								isInteractive
								isFixedWidth
								analyticsAttributes={{ cellType: 'rich-text' }}
							>
								{({ ref }) => (
									<Pressable xcss={richTextCellWrapperStyles} ref={ref} {...props}>
										{previewText}
									</Pressable>
								)}
							</HoverPopover>
						)}
					</CellHoverRenderer>
				</CellHoverProvider>
			);
		}

		if (isDensityFull) {
			return (
				<RichTextCellFull
					issueKey={issueKey}
					richText={richText?.adfValue?.json || ''}
					canBeExported={issueData?.canBeExported}
				/>
			);
		}

		return (
			<RichTextCellCompact
				fieldId={fieldId}
				plainText={richText?.adfValue?.convertedPlainText?.plainText || ''}
				issueKey={issueKey}
				shouldOpenOnFirstRender={shouldOpenOnFirstRender}
			/>
		);
	},
);

const richTextCellWrapperStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	cursor: 'pointer',
	display: 'block',
	backgroundColor: 'color.background.neutral.subtle',
	boxSizing: 'border-box',
	height: '100%',
	font: 'font.body',
	width: '100%',
	textAlign: 'left',
	borderWidth: '2px',
	borderStyle: 'solid',
	borderRadius: 'border.radius',
	// @ts-expect-error Transparent is not valid but should be
	borderColor: 'transparent',
	':focus': {
		borderColor: 'color.border.focused',
		outline: 'none',
	},
});
